import { Component, ErrorInfo, ReactNode } from 'react'

const RELOAD_FLAG = 'sgo-chunk-reload-attempted'

const isChunkLoadError = (error: Error): boolean =>
  error.name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/.test(error.message)

interface ChunkErrorBoundaryProps {
  children: ReactNode
}

interface ChunkErrorBoundaryState {
  hasError: boolean
}

/**
 * Após um deploy, abas já abertas referenciam chunks com hash antigo que não existem
 * mais no servidor. Nesse caso recarrega a página uma única vez (flag em sessionStorage
 * evita loop caso o chunk realmente não exista por outro motivo).
 */
export class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  state: ChunkErrorBoundaryState = { hasError: false }

  componentDidMount(): void {
    // Se o app montou com sucesso, a flag da tentativa anterior não é mais necessária.
    // Adia a limpeza para não abrir uma janela de loop caso o mesmo erro se repita de imediato.
    window.setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 10000)
  }

  static getDerivedStateFromError(): ChunkErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1')
        window.location.reload()
        return
      }
      return
    }
    // eslint-disable-next-line no-console
    console.error('Erro não tratado capturado pelo ChunkErrorBoundary:', error, errorInfo)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <p>Ocorreu um erro. Se a página não recarregar automaticamente, <a href={window.location.href}>clique aqui</a>.</p>
        </div>
      )
    }
    return this.props.children
  }
}
